import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { User, BadgeCheck, Share2 } from "lucide-react";
import { api } from "@/lib/api";
import { fileUrl } from "@/components/Certificate";
import { PublicHeader } from "@/components/Layout";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

export default function Team() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch on mount — api/setState refs are stable, no deps required.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    api.get("/staff")
      .then(({ data }) => setStaff(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <PublicHeader />
      <main className="w-full max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-blue-600 dark:text-blue-400 mb-3">The people behind CertiSeal</div>
          <h1 className="font-display font-black text-4xl md:text-6xl tracking-tight text-slate-900 dark:text-white leading-[1.05]" data-testid="team-title">
            Our Team
          </h1>
          <p className="mt-4 text-slate-600 dark:text-slate-400">
            A small team obsessed with restoring trust in academic credentials.
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="team-loading">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="p-6 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                <Skeleton className="w-full aspect-square rounded-lg mb-4" />
                <Skeleton className="h-5 w-3/4 mb-2" />
                <Skeleton className="h-3 w-1/2" />
              </Card>
            ))}
          </div>
        ) : staff.length === 0 ? (
          <Card className="p-12 text-center border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900" data-testid="team-empty">
            <User className="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <p className="text-slate-500 dark:text-slate-400">
              Team profiles are coming soon.
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="team-grid">
            {staff.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              >
                <StaffCard staff={s} />
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function StaffCard({ staff: s }) {
  const shareUrl = typeof window !== "undefined"
    ? `${window.location.origin}/team#staff-${s.id}`
    : `/team#staff-${s.id}`;

  const handleShare = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const payload = {
      title: `${s.name} · CertiSeal`,
      text: `${s.name} — ${s.position} at CertiSeal`,
      url: shareUrl,
    };
    // Native share sheet (mobile + some desktop browsers)
    if (navigator.share) {
      try {
        await navigator.share(payload);
        return;
      } catch {
        // User cancelled — silently ignore
        return;
      }
    }
    // Fallback: copy to clipboard
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Profile link copied to clipboard");
    } catch {
      toast.error("Unable to copy link");
    }
  };

  return (
    <Card
      id={`staff-${s.id}`}
      className="p-6 h-full border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:shadow-lg dark:hover:shadow-black/40 transition-shadow flex flex-col"
      data-testid={`team-card-${s.id}`}
    >
      {/* Photo + verified badge overlay */}
      <div className="relative w-full aspect-square rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-800 mb-4 flex items-center justify-center">
        {s.photo_path ? (
          <img src={fileUrl(s.photo_path)} alt={s.name} className="w-full h-full object-cover" />
        ) : (
          <User className="w-12 h-12 text-slate-300 dark:text-slate-600" />
        )}
        <span
          className="absolute top-2.5 right-2.5 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-wider shadow-lg shadow-emerald-500/30 ring-2 ring-white dark:ring-slate-900"
          data-testid={`team-verified-${s.id}`}
          title="Verified CertiSeal team member"
          aria-label="Verified"
        >
          <BadgeCheck className="w-3 h-3" strokeWidth={2.5} />
          Verified
        </span>
      </div>

      <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white leading-tight" data-testid={`team-name-${s.id}`}>
        {s.name}
      </h3>
      <p className="text-sm text-blue-600 dark:text-blue-400 mt-1 font-medium" data-testid={`team-position-${s.id}`}>
        {s.position}
      </p>

      <Button
        variant="outline"
        size="sm"
        onClick={handleShare}
        className="mt-4 w-full rounded-full border-slate-300 dark:border-slate-700 hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400 dark:hover:border-blue-500 transition-colors"
        data-testid={`team-share-${s.id}`}
        aria-label={`Share ${s.name}'s profile`}
      >
        <Share2 className="w-3.5 h-3.5 mr-1.5" /> Share profile
      </Button>
    </Card>
  );
}
